import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Checkbox, Select, Functions } from "rhombus-react";
import SoftmaxSelection from "widgets/modals/multi-variant/SoftmaxSelection";
import StatSigSelection from "widgets/modals/multi-variant/StatSigSelection";
import { isValidSelection } from "widgets/modals/multi-variant/MultiVariantModal";
import {
	TARGET_METHODS,
	WINNER_PERCENTAGE_DEFAULT,
	STATISTICAL_SIGNIFICANCE_DEFAULT,
	MIN_EVENTS_DEFAULT,
	TEMPERATURE_DEFAULT
} from "utils/constants";

const ROTATION_MODES = {
	EQUAL: "equal",
	MANUAL: "manual",
	AUTO_OPT: "auto_opt",
};

const METANODE_OPTIMIZATION_MODELS = [
	{ value: TARGET_METHODS.SOFTMAX, label: "New Modern" },
	{ value: TARGET_METHODS.STATISTICAL_SIGNIFICANCE, label: "Statistical Significance" },
];

function resolveInitialRotationMode(loadedValues) {
	if (!loadedValues || !Object.keys(loadedValues).length) { return ROTATION_MODES.EQUAL; }
	if (loadedValues?.options?.autoOptimization) { return ROTATION_MODES.AUTO_OPT; }
	if (loadedValues?.options?.weightColumn) { return ROTATION_MODES.MANUAL; }
	return ROTATION_MODES.EQUAL;
}

function buildAutoOptimization({ optimizationModel, selectedObjective, winnerPercentage, statSigPercentage, minEvents, temperature }) {
	const ao = {
		goal: optimizationModel,
		advertiserObjective: selectedObjective?.value || null,
	};

	if (optimizationModel === TARGET_METHODS.SOFTMAX) {
		ao.parameters = { type: TARGET_METHODS.SOFTMAX, temperature };
	} else if (optimizationModel === TARGET_METHODS.STATISTICAL_SIGNIFICANCE) {
		ao.parameters = { type: TARGET_METHODS.STATISTICAL_SIGNIFICANCE, winnerPercentage, statSigPercentage, minEvents };
	}

	return ao;
}

export { ROTATION_MODES };

export default function RotationModePanel({ loadedValues, columnOptions, onChange }) {
	const [rotationMode, setRotationMode] = useState(() => resolveInitialRotationMode(loadedValues));
	const [weightColumn, setWeightColumn] = useState(null);
	const [optimizationModel, setOptimizationModel] = useState(TARGET_METHODS.SOFTMAX);
	const [selectedObjective, setSelectedObjective] = useState(null);
	const [winnerPercentage, setWinnerPercentage] = useState(WINNER_PERCENTAGE_DEFAULT);
	const [statSigPercentage, setStatSigPercentage] = useState(STATISTICAL_SIGNIFICANCE_DEFAULT);
	const [minEvents, setMinEvents] = useState(MIN_EVENTS_DEFAULT);
	const [temperature, setTemperature] = useState(TEMPERATURE_DEFAULT);

	const isAutoOpt = rotationMode === ROTATION_MODES.AUTO_OPT;
	const isManual = rotationMode === ROTATION_MODES.MANUAL;

	useEffect(() => {
		if (!loadedValues || !Object.keys(loadedValues).length) { return; }
		const ao = loadedValues?.options?.autoOptimization;
		if (ao) {
			setRotationMode(ROTATION_MODES.AUTO_OPT);
			if (ao.goal) { setOptimizationModel(ao.goal); }
			if (ao.advertiserObjective) {
				setSelectedObjective({
					value: ao.advertiserObjective,
					label: `${ao.advertiserObjective.name} (${ao.advertiserObjective.id})`,
				});
			}
			if (ao.parameters) {
				if (ao.parameters.winnerPercentage !== null && ao.parameters.winnerPercentage !== undefined) { setWinnerPercentage(ao.parameters.winnerPercentage); }
				if (ao.parameters.statSigPercentage !== null && ao.parameters.statSigPercentage !== undefined) { setStatSigPercentage(ao.parameters.statSigPercentage); }
				if (ao.parameters.minEvents !== null && ao.parameters.minEvents !== undefined) { setMinEvents(ao.parameters.minEvents); }
				if (ao.parameters.temperature !== null && ao.parameters.temperature !== undefined) { setTemperature(ao.parameters.temperature); }
			}
		} else if (loadedValues?.options?.weightColumn) {
			setRotationMode(ROTATION_MODES.MANUAL);
			setWeightColumn(loadedValues.options.weightColumn);
		}
	}, [loadedValues]);

	useEffect(() => {
		if (isAutoOpt) {
			const isValid = isValidSelection({ targetMethod: optimizationModel, selectedObjective, winnerPercentage, statSigPercentage, minEvents, temperature });
			onChange({
				weightColumn: undefined,
				autoOptimization: buildAutoOptimization({ optimizationModel, selectedObjective, winnerPercentage, statSigPercentage, minEvents, temperature }),
				isValid,
			});
		} else if (isManual) {
			onChange({ weightColumn: weightColumn || undefined, autoOptimization: undefined, isValid: true });
		} else {
			onChange({ weightColumn: undefined, autoOptimization: undefined, isValid: true });
		}
	}, [rotationMode, weightColumn, optimizationModel, selectedObjective, winnerPercentage, statSigPercentage, minEvents, temperature]);

	return (
		<div className="metanode-rotation-mode">
			<div className="metanode-rotation-mode__label">
				Rotation Mode
			</div>
			<div className="metanode-rotation-mode__radios">
				<Checkbox
					type="radio"
					label="Equal weighting"
					checked={rotationMode === ROTATION_MODES.EQUAL}
					onChange={() => setRotationMode(ROTATION_MODES.EQUAL)}
				/>
				<Checkbox
					type="radio"
					label="Manual weighting"
					checked={rotationMode === ROTATION_MODES.MANUAL}
					onChange={() => setRotationMode(ROTATION_MODES.MANUAL)}
				/>
				<Checkbox
					type="radio"
					label="Auto-optimization"
					checked={rotationMode === ROTATION_MODES.AUTO_OPT}
					onChange={() => setRotationMode(ROTATION_MODES.AUTO_OPT)}
				/>
			</div>
			{isManual && (
				<div className="metanode-bulk-sheet-column">
					<Select
						label="Bulk Sheet Column"
						placeholder="Select column for manual rotations..."
						isClearable={false}
						onMenuScrollToBottom={Functions.noop}
						options={columnOptions}
						value={weightColumn ? { label: weightColumn, value: weightColumn } : null}
						onChange={val => setWeightColumn(val?.value || null)}
						required
						meta={{ valid: !!weightColumn, dirty: true }}
					/>
				</div>
			)}
			{isAutoOpt && (
				<div className="metanode-auto-opt-panel">
					<Select
						label="Optimization Model"
						options={METANODE_OPTIMIZATION_MODELS}
						value={METANODE_OPTIMIZATION_MODELS.find(opt => opt.value === optimizationModel)}
						onChange={val => setOptimizationModel(val.value)}
						isClearable={false}
						onMenuScrollToBottom={Functions.noop}
					/>
					{optimizationModel === TARGET_METHODS.SOFTMAX && (
						<SoftmaxSelection
							onObjective={setSelectedObjective}
							selectedObjective={selectedObjective}
							temperature={temperature}
							onTemperatureChange={setTemperature}
							showValidation
						/>
					)}
					{optimizationModel === TARGET_METHODS.STATISTICAL_SIGNIFICANCE && (
						<StatSigSelection
							onObjective={setSelectedObjective}
							selectedObjective={selectedObjective}
							winnerPercentage={winnerPercentage}
							onWinnerPercentageChange={setWinnerPercentage}
							statSigPercentage={statSigPercentage}
							onStatSigPercentageChange={setStatSigPercentage}
							minEvents={minEvents}
							onMinEventsChange={setMinEvents}
							showValidation
						/>
					)}
				</div>
			)}
		</div>
	);
}

RotationModePanel.propTypes = {
	loadedValues: PropTypes.object,
	columnOptions: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, value: PropTypes.string })),
	onChange: PropTypes.func.isRequired,
};
